import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage } from 'react-intl'
import { startCase } from 'lodash'
import { Form, FormGroup, Label } from 'reactstrap'
import { Modal, ModalActions } from 'components/Modal'
import Input from 'components/Input'
import InputFeedback from 'components/InputFeedback'
import messages from './messages'
import styles from './ActionsModal.scss'

const allowedModes = ['create', 'edit', 'delete', 'view']
const [MODE_CREATE, MODE_EDIT, MODE_DELETE, MODE_VIEW] = allowedModes

export default function ActionsModal(props) {
  const { type, mode = MODE_CREATE, item, primaryKey, fields = {}, onFieldChange, onClose, onSubmit, children } = props

  const [invalidValues, setInvalidValues] = useState({})

  const onSubmitForm = e => {
    e.preventDefault()
    onSubmit()
  }

  const onChange = (key, value) => {
    onFieldChange(key, value)

    const { validator = () => true } = fields[key]
    const isValid = validator(value)

    setInvalidValues({
      ...invalidValues,
      [key]: !isValid,
    })
  }

  const renderHeader = () => (
    <header className={styles.header}>
      <FormattedMessage
        {...messages.header}
        values={{
          mode,
          type,
          itemName: item[primaryKey],
        }}
        tagName='span'
      />
    </header>
  )

  const formActions = [
    {
      key: 'cancel',
      color: 'secondary',
      outline: true,
      label: <FormattedMessage {...messages.actions.cancel} />,
      onClick: onClose,
    },
    {
      key: 'submit',
      type: 'submit',
      color: 'success',
      outline: true,
      isDisplayed: mode !== MODE_VIEW,
      label: <FormattedMessage {...messages.actions.submit} />,
      disabled: Object.values(invalidValues).some(x => x),
      onClick: onSubmit,
    },
  ]

  return (
    <Modal isOpen size='lg' onClose={onClose} header={renderHeader()}>
      <div className={styles.container}>
        <Form onSubmit={onSubmitForm} className={styles.form}>
          {mode === MODE_DELETE && (
            <div className={styles.deleteHint}>
              <FormattedMessage
                {...messages.deleteHint}
                values={{
                  type,
                  itemName: <span className={styles.name}>{item[primaryKey]}</span>,
                }}
                tagName='span'
              />
            </div>
          )}
          {mode !== MODE_DELETE &&
            Object.values(fields)
              .filter(({ isDisplayed = true }) => isDisplayed)
              .map(
                ({
                  key,
                  isRequired = false,
                  isReadonly = false,
                  disabled = false,
                  formFeedback,
                  label,
                  type = 'text',
                  options,
                  renderInput,
                  placeholder,
                }) => {
                  const readonly = mode === MODE_VIEW || isReadonly || disabled

                  const hasValue = !readonly && !!item[key]
                  const isValid = hasValue && !invalidValues[key]
                  const isInvalid = hasValue && invalidValues[key]

                  return (
                    <FormGroup key={key} className={styles.group}>
                      <Label for={key} className={styles.label}>
                        {label}
                        {isRequired && (
                          <span className={styles.required} title='Required'>
                            *
                          </span>
                        )}
                      </Label>
                      {renderInput ? (
                        renderInput({ disabled: readonly })
                      ) : (
                        <>
                          <Input
                            id={key}
                            name={key}
                            type={type}
                            autoComplete='new-password'
                            readOnly={readonly}
                            disabled={readonly}
                            required={isRequired}
                            value={item[key]}
                            onChange={value => onChange(key, value)}
                            valid={isValid}
                            invalid={isInvalid}
                            placeholder={placeholder}
                            className={styles.input}
                          >
                            {options &&
                              options.map((option, index) => (
                                <option key={index} value={option.id}>
                                  {startCase(option.label) || startCase(option.name)}
                                </option>
                              ))}
                          </Input>
                          {formFeedback && isInvalid && (
                            <InputFeedback isDisplayed={isInvalid}>{formFeedback}</InputFeedback>
                          )}
                        </>
                      )}
                    </FormGroup>
                  )
                },
              )}
          {children}
          <ModalActions actions={formActions} />
        </Form>
      </div>
    </Modal>
  )
}

ActionsModal.propTypes = {
  type: PropTypes.string.isRequired,
  mode: PropTypes.oneOf(allowedModes),
  item: PropTypes.any.isRequired,
  primaryKey: PropTypes.string.isRequired,
  fields: PropTypes.object,
  onFieldChange: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  children: PropTypes.any,
}